import { CCard, CContainer, CRow } from "@coreui/react-pro";
import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Pagination } from "../../../core/interfaces";
import { useActionBarStore, useIdentityStore, usePaginationStore } from "../../../core/store";
import { Util } from "../../../core/util/util";
import { AppliedFilters, CellLink, CellPosition, Divider, RoundedSquareIcon, SearchTable } from "../../components";
import { LoaderContext } from "../../components/loader/context/LoaderContext";
import { GridLoader } from "react-spinners";
import NoItems from "../../components/ui/no-items/NoItems";

import { BASE_ROUTE, ROUTE_NAME_ID_2, RouteName } from "../../../core/constants/route_name";
import { DataGridPro, GridColDef, GridRenderCellParams, GridRowParams, GridSortModel, GridState } from '@mui/x-data-grid-pro';
import { TablePagination, useTheme } from "@mui/material";
import useMediaQuery from '@mui/material/useMediaQuery';
import moment from 'moment';
import { MyColumnMenu } from "../../components/table/mui/MyColumnMenu";
import { LOCAL_STORAGE_KEYS } from "../../../core/constants/local_storage";
import { useGridLocalStorage } from "../../components/table/hook/useGridLocalStorage";
import { useSponsor } from "../../../core/hooks/db/fisioteduca/useFisioteduca";
import { Sponsor } from "../../../core/models/db";
import CellDate from "../../components/table/cell-date/CellDate";
import { useTranslation } from "react-i18next";

/** Paginación inicial (modo servidor) */
// const INIT_PAGINATION: Pagination = { page: 0, itemsPerPage: 10, filters: {} };


const SponsorPage = () => {
    const { identity } = useIdentityStore();
    const navigate = useNavigate();

    const { t } = useTranslation();


    const {
        gridState,
        saveGridState,
        handleColumnResize } = useGridLocalStorage(LOCAL_STORAGE_KEYS.TABLE_SPONSORS);

    const { isShowActionBar, reset, loadActionBar, showActionBar,
        hideActionBar } = useActionBarStore(state => state);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // Detección mobile

    const INIT_PAGINATION = useMemo(() => ({
        page: 0,
        itemsPerPage: 10,
        filters: {},
        orderBy: null,
    }), []);


    const { pagination, setPagination, resetFilterRaws } = usePaginationStore();
    const [init, setInit] = useState(false);

    const [body, setBody] = useState<Sponsor[]>([]);
    const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);

    const { getSponsors } = useSponsor();

    // 1) Setup paginación inicial + reset de filtros
    useEffect(() => {
        setPagination({
            ...INIT_PAGINATION,
        });
        resetFilterRaws();
        setInit(true);
    }, []);

    useEffect(() => {
        if (selectedRowIds.length > 0) {
            const roleAllowed = ["ROLE_ADMIN", "ROLE_DEVELOPER", "ROLE_MANAGER"];

            loadActionBar({
                delete: roleAllowed.includes(identity.role),
                // userBlocked: identity_initPage.role === "ROLE_ADMIN" || identity_initPage.role === "ROLE_DEVELOPER",
                table: "sponsor",
                count: selectedRowIds.length,
                items: selectedRowIds
            });
            setLoading(false);

            showActionBar();
        } else {
            hideActionBar();
        }
    }, [selectedRowIds]);

    // 2) Cargar la lista cuando cambien paginación / filtros
    useEffect(() => {
        if (!init) return;
        loadData(pagination);
    }, [
        init,
        pagination?.page,
        pagination?.itemsPerPage,
        JSON.stringify(pagination?.orderBy),
        JSON.stringify(pagination?.filters),
    ]);

    // 3) Si se pide reset
    useEffect(() => {
        console.log("reset", reset);
        if (reset) {
            console.log("entro para iniciar");
            loadData({ ...INIT_PAGINATION });
            setSelectedRowIds([]);
        }
    }, [reset]);

    // 4) Lógica para cargar datos (similar a `initPage`)
    async function loadData(pag = pagination) {
        setLoading(true);
        try {
            let pagAux = { ...pag, page: pag.page + 1 };
            pagAux.filters = {
                ...pag.filters,
                // idCompanyFk: {
                //     value: identity?.idCompanySelected,
                // },
            };
            // hasUser = true
            const response = await getSponsors(pagAux);
            if (response?.ok) {
                const rows = response.item.rows.map((est) => {
                    let phoneComplete = "";
                    if (est.phoneCode && est.phoneNumber) {
                        phoneComplete = `(+${est.phoneCode}) ${est.phoneNumber}`;
                    }
                    return {
                        ...est,
                        phoneComplete: phoneComplete || "No hay teléfono registrado",
                        userCount: est.userCount || 0,
                    };
                });
                setBody(rows);

                setPagination({
                    ...pag,
                    totalItems: response.item.pagination.totalItems,
                    totalPages: response.item.pagination.totalPages,
                });
            } else {
                console.warn("Problema al devolver establecimientos");
            }


        } catch (err) {
            console.error(err);
        }
        setLoading(false);
    }

    // 5) Columnas DataGridPro
    const columns: GridColDef[] = useMemo(() => {
        return [
            {
                field: "title",
                headerName: t("screens.sponsor.table.title") || "Title",
                flex: isMobile ? 1 : 2, // Ajuste dinámico del flex
                minWidth: isMobile ? 150 : 150, // Ancho mínimo para evitar columnas demasiado estrechas
                sortable: true,
            },
            {
                field: "code",
                type: "string",
                align: "center",
                headerAlign: "center",
                headerName: t("screens.sponsor.table.code") || "Code",
                width: isMobile ? 140 : 120, // Ancho ajustado para móviles y escritorio
                flex: isMobile ? undefined : 1, // Solo aplicar flex en escritorio
                sortable: true,
            },
            {
                field: "externalLink",
                type: "string",
                align: "left",
                headerAlign: "left",
                headerName: t("screens.sponsor.table.externalLink") || "External Link",
                width: isMobile ? 140 : 140, // Ancho ajustado para móviles y escritorio
                flex: isMobile ? undefined : 1, // Solo aplicar flex en escritorio
                sortable: true,
                renderCell: (params: GridRenderCellParams<any, string>) => (
                    <CellLink url={params.value} label={params.value} />
                ),
            },
            {
                field: "position",
                headerName: t("screens.sponsor.table.position") || "Position",
                type: "number",
                align: "center",
                headerAlign: "center",
                width: isMobile ? 120 : 120, // Ancho ajustado para móviles y escritorio
                flex: isMobile ? undefined : 1, // Solo aplicar flex en escritorio
                sortable: true,
                renderCell: (params: GridRenderCellParams<Sponsor, number>) => (
                    <CellPosition position={params.value} />
                ),
            },
            {
                field: "createdDate",
                headerName: t("screens.sponsor.table.createdDate") || "Created Date",
          
                align: "center",
                headerAlign: "center",
                flex: isMobile ? 1 : 1, // Flex igual en ambos
                minWidth: isMobile ? 120 : 160, // Ancho mínimo para evitar columnas demasiado estrechas
                sortable: false,
                renderCell: (params: GridRenderCellParams<Sponsor, string>) => (
                    <CellDate startDate={params.value} hasTime={false} />
                ),
            },
            // Opcional: Agregar más columnas y ocultarlas en móviles si no son esenciales
            // {
            //   field: "description",
            //   headerName: t("screens.sponsor.table.description") || "Description",
            //   flex: 2,
            //   sortable: false,
            //   hide: isMobile, // Ocultar en móviles
            //   renderCell: (params: GridRenderCellParams<Sponsor, string>) => (
            //     <span>{params.value}</span>
            //   ),
            // },
        ];
    }, [isMobile, t]);

    // 6) Convierto `body` en `rows` con ID
    const rows = useMemo(
        () => body.map((est) => ({ ...est, id: est.id })),
        [body]
    );

    // 7) Manejo de la paginación en DataGridPro
    const handlePaginationModelChange = (model: { page: number; pageSize: number }) => {
        if (model.pageSize !== pagination?.itemsPerPage) {
            setPagination({ ...pagination, page: 0, itemsPerPage: model.pageSize });
        } else if (model.page !== pagination?.page) {
            setPagination({ ...pagination, page: model.page });
        }
    };

    // 8) Manejo de sort en DataGridPro
    const handleSortModelChange = (sortModel: GridSortModel) => {
        if (sortModel.length > 0) {
            const { field, sort } = sortModel[0];

            if (field === "phoneComplete") {
                setPagination({
                    ...pagination,
                    orderBy: { field: "phoneNumber", order: sort },
                });
            } else {
                setPagination({
                    ...pagination,
                    orderBy: { field, order: sort },
                });
            }

        } else {
            setPagination({
                ...pagination,
                orderBy: null,
            });
        }
    };

    // 9) Click en fila => si no hay filas seleccionadas, goToUpdate
    const goToUpdate = (item: Sponsor) => {
        const roleAllowed = ["ROLE_ADMIN", "ROLE_DEVELOPER"];
        if (identity && roleAllowed.includes(identity.role)) {
            const pagesRoutes = t("routes", { returnObjects: true });
            const newRoute = ROUTE_NAME_ID_2({ id: item.id }, pagesRoutes, RouteName.sponsorDetail);
            navigate(`${BASE_ROUTE}${newRoute}`, { state: { id: item.id } });
        }
    };

    // 10) Agregar nuevo
    const goToAdd = () => {
        const pagesRoutes = t("routes", { returnObjects: true });
        const newRoute = ROUTE_NAME_ID_2({}, pagesRoutes, RouteName.sponsorAdd);
        navigate(`${BASE_ROUTE}${newRoute}`);
    };


    const initialState = useMemo(() => ({
        pinnedColumns: gridState.pinnedColumns,
        columns: {
            orderedFields: gridState.columnOrder,
            columnWidths: gridState.columnWidths, // Aplicar anchos persistidos
        },
    }), [gridState.pinnedColumns, gridState.columnOrder, gridState.columnWidths]);


    return (
        <CContainer>
            <div className="flex">
                <div className="w-full flex justify-between items-center pt-4 pb-5">
                    <div>
                        <span className="text-2xl sm:text-4xl" style={{ fontWeight: 500 }}>
                            {t("screens.sponsor.title")}
                        </span>
                    </div>
                    <div
                        onClick={goToAdd}
                        className="px-3 py-1 rounded-xl flex items-center bg-principalColor text-white hover:bg-principalColor-500 hover:cursor-pointer"
                    >
                        <span className="text-2xl mr-2">+</span> <span>Agregar</span>
                    </div>
                </div>
            </div>

            <CCard className="rounded-xl p-3">
                {/* Filtros y búsquedas */}
                {/* <CRow>
                    <SearchTable nameColumn={["title"]} label={["Nombre"]} />
                </CRow>
                <Divider marginX={-16} />
                <CRow>
                    <AppliedFilters />
                </CRow>
                <Divider marginX={-16} /> */}
                <CRow>
                    {loading && (
                        <div className="w-full h-[250px] flex justify-center items-center">
                            <GridLoader color="#3d4f65" size={15} speedMultiplier={0.7} />
                        </div>
                    )}

                    {body && body.length > 0 && pagination && pagination?.totalPages && (
                        //                         //   className={width < 780 ? 'table-responsive' : ''}
                        <div style={{ visibility: loading ? 'hidden' : 'visible', height: loading ? '0px' : 'auto' }}>
                            <DataGridPro
                                localeText={{
                                    columnMenuHideColumn: t('tableGeneral.columnMenuHideColumn'),
                                    columnMenuUnsort: t('tableGeneral.columnMenuUnsort'),
                                    columnMenuSortAsc: t('tableGeneral.columnMenuSortAsc'),
                                    columnMenuSortDesc: t('tableGeneral.columnMenuSortDesc'),
                                    columnMenuManageColumns: t('tableGeneral.columnMenuManageColumns'),

                                    columnsManagementReset: t('tableGeneral.columnsManagementReset'),
                                    pinToLeft: t('tableGeneral.pinToLeft'),
                                    pinToRight: t('tableGeneral.pinToRight'),
                                    unpin: t('tableGeneral.unpin'),

                                }}
                                slots={{
                                    columnMenu: MyColumnMenu,
                                }}

                                initialState={initialState}



                                onStateChange={(newState: GridState) => {
                                    // console.log("newState", newState);
                                    // Extraemos lo que nos interesa
                                    const nextColumnOrder = newState.columns.orderedFields;
                                    // const nextVisibility = newState.columns.visibilityModel;
                                    const nextPinned = newState.pinnedColumns;

                                    // Guardamos en localStorage
                                    saveGridState({
                                        columnOrder: nextColumnOrder,
                                        // columnVisibilityModel: nextVisibility,
                                        pinnedColumns: nextPinned,
                                    });
                                }}


                                disableColumnResize={false}
                                onColumnResize={(params) => {
                                    // handleColumnResize(params); // Llamamos al manejador del hook
                                }}


                                rows={rows}
                                columns={columns}
                                // Selección con check
                                checkboxSelection
                                // Si quieres ocultar el “Select all”:
                                // => una forma es definir la columna con headerCheckboxSelection: false
                                // => otra forma: en sx -> display: none de la clase
                                // => aquí lo mostramos, pero si no quieres, ajusta
                                //   or "headerCheckboxSelectionFilteredOnly" en la col especial

                                disableRowSelectionOnClick={false}
                                onRowClick={(params: GridRowParams, event) => {
                                    // Si hay seleccion, dejamos que se maneje la (des)selección
                                    if (selectedRowIds.length === 0) {
                                        // No hay check => edit
                                        event.stopPropagation();
                                        goToUpdate(params.row as Sponsor);
                                    }
                                }}
                                rowSelectionModel={selectedRowIds}
                                onRowSelectionModelChange={(newSelection) => {
                                    console.log("newSelection", newSelection);
                                    // Detectar “Select All” => si newSelection es algo especial, cargar todos IDs del servidor
                                    // if newSelection ... => getAllIdSponsor => setSelectedRowIds
                                    setSelectedRowIds(newSelection as string[]);
                                }}

                                // Paginación modo servidor
                                pagination
                                paginationMode="server"
                                rowCount={pagination.totalItems}
                                pageSizeOptions={[5, 10, 25, 50, 100]}
                                paginationModel={{
                                    page: pagination?.page,
                                    pageSize: pagination?.itemsPerPage,
                                }}
                                onPaginationModelChange={handlePaginationModelChange}

                                // Ordenamiento
                                sortingMode="server"
                                onSortModelChange={handleSortModelChange}

                                // 3) Reemplazar los íconos del checkbox
                                slotProps={{
                                    baseCheckbox: {
                                        icon: (
                                            <RoundedSquareIcon
                                                checked={false}
                                                color={undefined}
                                            />
                                        ),
                                        checkedIcon: (
                                            <RoundedSquareIcon
                                                checked
                                                color={undefined}
                                            />
                                        ),
                                        indeterminateIcon: <RoundedSquareIcon indeterminate />,
                                        style: { padding: 0 },
                                    },
                                }}

                                sx={{
                                    border: 0,
                                    '& .MuiDataGrid-columnHeaders': {
                                        borderBottom: 'none',
                                    },
                                    '& .MuiDataGrid-row': {
                                        borderBottom: 'none',
                                    },
                                    // Quitar separadores
                                    '& .MuiDataGrid-columnSeparator': {
                                        visibility: 'hidden',
                                    },
                                    '& .MuiDataGrid-columnHeader:hover .MuiDataGrid-columnSeparator': {
                                        visibility: 'visible',
                                    },
                                    '& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnHeaderTitleContainer': {
                                        display: 'none',
                                    },
                                }}
                            />
                        </div>
                    )}

                    {!loading && rows.length === 0 && <NoItems />}
                </CRow>



            </CCard>
        </CContainer>
    );
};

export default SponsorPage;